import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, BookText, ScrollText, Languages, BookmarkPlus } from 'lucide-react';
import useTranslate from '../hooks/useTranslate';
import { localizeReference } from '../utils/bookTranslations';
import { LS } from '../utils/lang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders the public-domain Matthew Henry's Commentary for a given
 * book + chapter. Pulls from `/api/commentary/matthew-henry`.
 *
 * Two editions toggle in-place:
 *   - **Concise** (default, ~3.5 MB) — section-by-section per verse range
 *   - **Complete** (~40 MB) — unabridged paragraph-flow text
 */
const MatthewHenryCommentary = ({ book, chapter, language = 'en', compact = false, onSaveExcerpt }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('concise');
  const mhCardRef = useRef(null);

  useEffect(() => {
    if (!book || !chapter) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${BACKEND_URL}/api/commentary/matthew-henry`, {
        params: { book, chapter, source },
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Commentary not available');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [book, chapter, source]);

  // Translate the public-domain English bodies into the active language.
  // Order: [intro, full_text, ...section commentaries] — indexed back below.
  const blocks = React.useMemo(() => {
    if (!data) return [];
    const arr = [data.intro || '', data.full_text || ''];
    (data.sections || []).forEach((s) => arr.push(s.commentary || ''));
    return arr;
  }, [data]);
  const { output: tBlocks, loading: tLoading, showOriginal, setShowOriginal, isTranslated } =
    useTranslate(blocks, language);
  const tIntro = tBlocks[0] ?? (data?.intro || '');
  const tFullText = tBlocks[1] ?? (data?.full_text || '');
  const sectionText = (i) => tBlocks[2 + i] ?? ((data?.sections || [])[i]?.commentary || '');

  const buildMhText = () => {
    if (!data) return '';
    if (source === 'complete') return (tFullText || data.full_text || '').trim();
    const parts = [];
    if (tIntro) parts.push(tIntro.trim());
    (data.sections || []).forEach((s, i) => {
      const body = sectionText(i);
      if (body) parts.push(`${language === 'es' ? 'V.' : 'V.'} ${s.verses}\n${body.trim()}`);
    });
    return parts.join('\n\n');
  };

  const mhSelRef = useRef('');

  const handleSaveExcerpt = () => {
    if (!onSaveExcerpt) return;
    const picked = mhSelRef.current;
    mhSelRef.current = '';
    onSaveExcerpt(picked || buildMhText());
  };

  const snapshotMhSelection = () => {
    const sel = window.getSelection ? window.getSelection() : null;
    if (sel && !sel.isCollapsed && sel.rangeCount && mhCardRef.current
        && sel.anchorNode && sel.focusNode
        && mhCardRef.current.contains(sel.anchorNode) && mhCardRef.current.contains(sel.focusNode)) {
      mhSelRef.current = sel.toString().trim();
    } else {
      mhSelRef.current = '';
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-6 text-muted-foreground text-sm"
        data-testid="mh-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        {language === 'es' ? 'Cargando Matthew Henry…' : 'Loading Matthew Henry…'}
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="border-dashed" data-testid="mh-empty">
        <CardContent className="p-4 text-xs text-muted-foreground flex items-center gap-2">
          <ScrollText className="w-4 h-4" />
          {language === 'es'
            ? 'Comentario de Matthew Henry no disponible para este capítulo.'
            : 'Matthew Henry commentary not available for this chapter.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={mhCardRef}
      className="border-amber-200/60 dark:border-amber-800/50 bg-gradient-to-br from-amber-50/50 to-orange-50/30 dark:from-amber-950/30 dark:to-orange-950/20"
      data-testid="mh-card"
    >
      <CardContent className={compact ? 'p-3 space-y-3' : 'p-4 sm:p-5 space-y-4'}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <BookText className="w-4 h-4 text-amber-700 dark:text-amber-300" />
              <h3 className="font-bold text-amber-900 dark:text-amber-100 text-sm sm:text-base">
                {language === 'es'
                  ? `Matthew Henry · ${source === 'complete' ? 'Comentario Completo' : 'Comentario Conciso'}`
                  : `Matthew Henry · ${source === 'complete' ? 'Complete Commentary' : 'Concise Commentary'}`}
              </h3>
            </div>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5 italic">
              {localizeReference(`${data.book} ${data.chapter}`, LS(false, true))}
              {source === 'concise' && (
                <>
                  {' · '}
                  {data.sections?.length || 0} {language === 'es' ? 'secciones' : 'sections'}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Source toggle: Concise ↔ Complete */}
            <div
              className="inline-flex rounded-md border border-amber-400/60 dark:border-amber-700 overflow-hidden text-[11px]"
              data-testid="mh-source-toggle"
            >
              <Button
                onClick={() => setSource('concise')}
                size="sm"
                variant="ghost"
                className={`h-6 px-2 rounded-none font-semibold ${
                  source === 'concise'
                    ? 'bg-amber-700 text-white hover:bg-amber-700 hover:text-white'
                    : 'text-amber-800 hover:bg-amber-100/60 dark:text-amber-200 dark:hover:bg-amber-900/40'
                }`}
                data-testid="mh-source-concise-btn"
              >
                {language === 'es' ? 'Conciso' : 'Concise'}
              </Button>
              <Button
                onClick={() => setSource('complete')}
                size="sm"
                variant="ghost"
                className={`h-6 px-2 rounded-none font-semibold ${
                  source === 'complete'
                    ? 'bg-amber-700 text-white hover:bg-amber-700 hover:text-white'
                    : 'text-amber-800 hover:bg-amber-100/60 dark:text-amber-200 dark:hover:bg-amber-900/40'
                }`}
                data-testid="mh-source-complete-btn"
              >
                {language === 'es' ? 'Completo' : 'Complete'}
              </Button>
            </div>
            <Badge
              variant="outline"
              className="border-amber-400/60 text-amber-800 dark:text-amber-200 text-[10px]"
            >
              {language === 'es' ? 'Dominio público' : 'Public Domain'}
            </Badge>
            {onSaveExcerpt && (
              <Button
                onPointerDown={snapshotMhSelection}
                onClick={handleSaveExcerpt}
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] font-semibold text-amber-800 hover:bg-amber-100/60 dark:text-amber-200 dark:hover:bg-amber-900/40"
                data-testid="save-excerpt-btn-matthew-henry"
              >
                <BookmarkPlus className="w-3 h-3 mr-1" />
                {language === 'es' ? 'Guardar' : 'Save'}
              </Button>
            )}
            {isTranslated && (
              <Button
                onClick={() => setShowOriginal((v) => !v)}
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] font-semibold text-amber-800 hover:bg-amber-100/60 dark:text-amber-200 dark:hover:bg-amber-900/40"
                data-testid="mh-translate-toggle"
                disabled={tLoading}
              >
                {tLoading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Languages className="w-3 h-3 mr-1" />
                )}
                {showOriginal ? 'Ver traducción' : 'Ver original'}
              </Button>
            )}
          </div>
        </div>

        {data.intro && (
          <div className="text-sm leading-relaxed text-amber-950 dark:text-amber-100/90 italic border-l-2 border-amber-400/60 pl-3">
            {tIntro}
          </div>
        )}

        {/* Complete edition — render the full chapter as a paragraph-flow blob */}
        {source === 'complete' && data.full_text && (
          <div
            className="text-sm leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap"
            data-testid="mh-full-text"
          >
            {tFullText}
          </div>
        )}

        {/* Concise edition — render section-by-section */}
        {source === 'concise' && (!data.sections || data.sections.length === 0) && (
          <p className="text-xs text-muted-foreground">
            {language === 'es'
              ? 'No hay secciones detalladas para este capítulo.'
              : 'No detailed sections for this chapter.'}
          </p>
        )}

        {source === 'concise' && (
          <div className="space-y-3">
            {(data.sections || []).map((s, i) => (
              <div
                key={`${s.verses}-${i}`}
                className="space-y-1.5"
                data-testid={`mh-section-${s.verses.replace(/[^\w-]/g, '_')}`}
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <Badge className="bg-amber-700 hover:bg-amber-700 text-white text-[10px] tabular-nums">
                    {language === 'es' ? 'V' : 'V.'} {s.verses}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                  {sectionText(i)}
                </p>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-amber-800/70 dark:text-amber-200/70 italic pt-2 border-t border-amber-200/60 dark:border-amber-800/50">
          {language === 'es'
            ? `Texto del Comentario ${source === 'complete' ? 'Completo' : 'Conciso'} de Matthew Henry — Dominio Público.`
            : `Text from Matthew Henry\u2019s ${source === 'complete' ? 'Complete' : 'Concise'} Commentary — Public Domain.`}
        </p>
      </CardContent>
    </Card>
  );
};

export default MatthewHenryCommentary;
